// src/hooks/useAuth.ts (Fixed)
import { useState, useEffect, useCallback } from 'react';
import { authService } from '@/services/auth';
import type { AuthState, LoginCredentials, SignUpData } from '@/types/auth';

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((user) => {
      setAuthState(prev => ({
        ...prev,
        user,
        loading: false,
        error: null
      }));
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      const user = await authService.signInWithGoogle();
      setAuthState({ user, loading: false, error: null });
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Google sign in failed'
      }));
    }
  }, []);

  const signInWithEmail = useCallback(async (credentials: LoginCredentials) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      const user = await authService.signInWithEmail(credentials);
      setAuthState({ user, loading: false, error: null });
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Email sign in failed'
      }));
    }
  }, []);

  const signUpWithEmail = useCallback(async (data: SignUpData) => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      const user = await authService.signUpWithEmail(data);
      setAuthState({ user, loading: false, error: null });
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Sign up failed'
      }));
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));
      await authService.signOut();
      setAuthState({ user: null, loading: false, error: null });
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Sign out failed'
      }));
    }
  }, []);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    return await authService.getIdToken();
  }, []);

  return {
    ...authState,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    getIdToken,
    isAuthenticated: !!authState.user
  };
};